import React, { useState, useEffect } from 'react';
import {
  X, FileText, DollarSign, Calendar, Shield,
  Monitor, MessageSquare, Award, ChevronLeft, Send,
  AlertCircle, Search, User,
} from 'lucide-react';
import { solicitudesAPI } from '../../../api/solicitudes';
import { employeesAPI } from '../../../api/employees';
import api from '../../../api/config';
import { usePermissionsContext } from '../../../contexts/PermissionsContext';

// ── Configuración de categorías ───────────────────────────────
const CATEGORIA_CFG = {
  certificaciones: { label: 'Certificaciones',  Icon: FileText,      color: 'bg-blue-50 text-blue-700 border-blue-200'    },
  prestamos:       { label: 'Préstamos',         Icon: DollarSign,    color: 'bg-green-50 text-green-700 border-green-200'  },
  vacaciones:      { label: 'Vacaciones',        Icon: Calendar,      color: 'bg-amber-50 text-amber-700 border-amber-200' },
  novedades:       { label: 'Novedades',         Icon: Calendar,      color: 'bg-orange-50 text-orange-700 border-orange-200' },
  disciplinarias:  { label: 'Disciplinarias',    Icon: AlertCircle,   color: 'bg-red-50 text-red-700 border-red-200'       },
  quejas:          { label: 'Quejas / Sugerencias', Icon: MessageSquare, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  seguro:          { label: 'Seguro Médico',     Icon: Shield,        color: 'bg-teal-50 text-teal-700 border-teal-200'    },
  sistemas:        { label: 'Sistemas / IT',     Icon: Monitor,       color: 'bg-gray-50 text-gray-700 border-gray-200'    },
};

// ── Campos dinámicos por tipo ─────────────────────────────────
function CamposEspecificos({ tipo, detalles, onChange, solicitudBase, onBaseChange, dependientes = [] }) {
  if (!tipo) return null;
  const d = detalles;
  const s = solicitudBase;

  const set = (k, v) => onChange({ ...d, [k]: v });
  const setBase = (k, v) => onBaseChange({ ...s, [k]: v });

  const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400';
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1';

  if (tipo.codigo?.startsWith('CERT_')) {
    return (
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Entidad destino (opcional)</label>
          <input type="text" placeholder="Banco, Embajada, etc." value={d.entidad_destino || ''} onChange={e => set('entidad_destino', e.target.value)} className={inputCls} />
        </div>
        {tipo.codigo === 'CERT_BANCO' && (
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={!!d.incluir_salario} onChange={e => set('incluir_salario', e.target.checked)} className="rounded" />
            Incluir salario en la carta
          </label>
        )}
      </div>
    );
  }

  if (tipo.codigo?.startsWith('PRES_')) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Monto solicitado (RD$) *</label>
          <input type="number" min="0" value={s.monto_solicitado || ''} onChange={e => setBase('monto_solicitado', e.target.value)} className={inputCls} placeholder="0.00" />
        </div>
        <div>
          <label className={labelCls}>Plazo en meses *</label>
          <input type="number" min="1" max="60" value={s.plazo_meses || ''} onChange={e => setBase('plazo_meses', e.target.value)} className={inputCls} placeholder="12" />
        </div>
        <div>
          <label className={labelCls}>Tasa de interés (%)</label>
          <input type="number" min="0" step="0.1" value={d.tasa_interes || ''} onChange={e => set('tasa_interes', e.target.value)} className={inputCls} placeholder="0" />
        </div>
        <div>
          <label className={labelCls}>Quincena de descuento</label>
          <select value={d.quincena_descuento || 'Q2'} onChange={e => set('quincena_descuento', e.target.value)} className={inputCls}>
            <option value="Q1">Primera quincena</option>
            <option value="Q2">Segunda quincena</option>
          </select>
        </div>
      </div>
    );
  }

  if (tipo.codigo === 'VAC_SOLICITUD') {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Fecha inicio *</label>
          <input type="date" value={s.fecha_inicio || ''} onChange={e => setBase('fecha_inicio', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Fecha fin *</label>
          <input type="date" value={s.fecha_fin || ''} onChange={e => setBase('fecha_fin', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Fecha retorno *</label>
          <input type="date" value={s.fecha_retorno || ''} onChange={e => setBase('fecha_retorno', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Días solicitados</label>
          <input type="number" min="1" value={d.dias_solicitados || ''} onChange={e => set('dias_solicitados', e.target.value)} className={inputCls} />
        </div>
      </div>
    );
  }

  if (tipo.codigo?.startsWith('NOV_')) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Fecha inicio *</label>
            <input type="date" value={s.fecha_inicio || ''} onChange={e => setBase('fecha_inicio', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Fecha fin *</label>
            <input type="date" value={s.fecha_fin || ''} onChange={e => setBase('fecha_fin', e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Días solicitados</label>
          <input type="number" min="1" value={d.dias_solicitados || ''} onChange={e => set('dias_solicitados', e.target.value)} className={inputCls} />
        </div>
        {tipo.codigo === 'NOV_LIC_MEDICA' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Nombre del médico</label>
              <input type="text" value={d.nombre_medico || ''} onChange={e => set('nombre_medico', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Diagnóstico</label>
              <input type="text" value={d.diagnostico || ''} onChange={e => set('diagnostico', e.target.value)} className={inputCls} />
            </div>
          </div>
        )}
        {tipo.codigo === 'NOV_FUNERAL' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Nombre del fallecido</label>
              <input type="text" value={d.nombre_fallecido || ''} onChange={e => set('nombre_fallecido', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Parentesco</label>
              <input type="text" value={d.parentesco || ''} onChange={e => set('parentesco', e.target.value)} className={inputCls} />
            </div>
          </div>
        )}
      </div>
    );
  }

  if (tipo.codigo === 'DISC_AMONESTACION') {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Tipo de amonestación *</label>
            <select value={d.tipo_amonestacion || 'escrita'} onChange={e => set('tipo_amonestacion', e.target.value)} className={inputCls}>
              <option value="verbal">Verbal</option>
              <option value="escrita">Escrita</option>
              <option value="suspension">Suspensión</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Gravedad *</label>
            <select value={d.gravedad || 'leve'} onChange={e => set('gravedad', e.target.value)} className={inputCls}>
              <option value="leve">Leve</option>
              <option value="moderada">Moderada</option>
              <option value="grave">Grave</option>
              <option value="muy_grave">Muy grave</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Fecha del incidente *</label>
            <input type="date" value={d.fecha_incidente || ''} onChange={e => set('fecha_incidente', e.target.value)} className={inputCls} />
          </div>
          {d.tipo_amonestacion === 'suspension' && (
            <div>
              <label className={labelCls}>Días de suspensión</label>
              <input type="number" min="1" value={d.dias_suspension || ''} onChange={e => set('dias_suspension', e.target.value)} className={inputCls} />
            </div>
          )}
        </div>
        <div>
          <label className={labelCls}>Motivo detallado *</label>
          <textarea rows={3} value={d.motivo || ''} onChange={e => set('motivo', e.target.value)} className={`${inputCls} resize-none`} placeholder="Describe el motivo de la amonestación..." />
        </div>
        <div>
          <label className={labelCls}>Lugar del incidente</label>
          <input type="text" value={d.lugar_incidente || ''} onChange={e => set('lugar_incidente', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Medida correctiva</label>
          <textarea rows={2} value={d.medida_correctiva || ''} onChange={e => set('medida_correctiva', e.target.value)} className={`${inputCls} resize-none`} />
        </div>
      </div>
    );
  }

  if (tipo.codigo === 'SEG_INCLUSION') {
    const HEALTH_TYPES = ['conyuge', 'hijo', 'hija'];
    const isAdicional = d.tipo_dependiente && !HEALTH_TYPES.includes(d.tipo_dependiente);
    return (
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className={labelCls}>Nombre completo del dependiente *</label>
          <input type="text" value={d.nombre_dependiente || ''} onChange={e => set('nombre_dependiente', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Tipo de dependiente *</label>
          <select value={d.tipo_dependiente || ''} onChange={e => set('tipo_dependiente', e.target.value)} className={inputCls}>
            <option value="">Seleccionar...</option>
            <option value="conyuge">Cónyuge</option>
            <option value="hijo">Hijo/a</option>
            <option value="padre">Padre/Madre</option>
            <option value="otro">Otros</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Parentesco</label>
          <input type="text" value={d.parentesco || ''} onChange={e => set('parentesco', e.target.value)} className={inputCls} placeholder="ej. Esposa, Hijo" />
        </div>
        <div>
          <label className={labelCls}>Cédula</label>
          <input type="text" value={d.cedula_dependiente || ''} onChange={e => set('cedula_dependiente', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Fecha de nacimiento</label>
          <input type="date" value={d.fecha_nacimiento_dependiente || ''} onChange={e => set('fecha_nacimiento_dependiente', e.target.value)} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Teléfono</label>
          <input type="text" value={d.telefono_dependiente || ''} onChange={e => set('telefono_dependiente', e.target.value)} className={inputCls} placeholder="809-000-0000" />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Dirección</label>
          <input type="text" value={d.direccion_dependiente || ''} onChange={e => set('direccion_dependiente', e.target.value)} className={inputCls} />
        </div>
        {isAdicional && (
          <div>
            <label className={labelCls}>Quincena de descuento</label>
            <select value={d.quincena_dependiente || 'AMBAS'} onChange={e => set('quincena_dependiente', e.target.value)} className={inputCls}>
              <option value="Q1">Primera quincena</option>
              <option value="Q2">Segunda quincena</option>
              <option value="AMBAS">Ambas quincenas</option>
            </select>
          </div>
        )}
      </div>
    );
  }

  if (tipo.codigo === 'SEG_EXCLUSION') {
    return (
      <div className="space-y-3">
        {dependientes.length > 0 ? (
          <div>
            <label className={labelCls}>Dependiente a excluir *</label>
            <select
              value={d.related_person_id || ''}
              onChange={e => {
                const sel = dependientes.find(dep => dep.id === e.target.value);
                onChange({
                  ...d,
                  related_person_id: e.target.value,
                  nombre_dependiente: sel ? `${sel.first_name} ${sel.last_name}`.trim() : (d.nombre_dependiente || ''),
                });
              }}
              className={inputCls}
            >
              <option value="">Seleccionar dependiente...</option>
              {dependientes.map(dep => (
                <option key={dep.id} value={dep.id}>
                  {dep.first_name} {dep.last_name} — {dep.relationship}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div>
            <label className={labelCls}>Nombre del dependiente a excluir *</label>
            <input type="text" value={d.nombre_dependiente || ''} onChange={e => set('nombre_dependiente', e.target.value)} className={inputCls} placeholder="No se encontraron dependientes registrados" />
          </div>
        )}
      </div>
    );
  }

  if (tipo.codigo?.startsWith('SIS_')) {
    return (
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Sistema / Equipo solicitado</label>
          <input type="text" value={d.sistema_nombre || ''} onChange={e => set('sistema_nombre', e.target.value)} className={inputCls} placeholder="Nombre del sistema, equipo o servicio..." />
        </div>
        {(tipo.codigo === 'SIS_LAPTOP' || tipo.codigo === 'SIS_FLOTA') && (
          <div>
            <label className={labelCls}>Especificaciones</label>
            <textarea rows={2} value={d.especificaciones || ''} onChange={e => set('especificaciones', e.target.value)} className={`${inputCls} resize-none`} placeholder="Descripción de requerimientos..." />
          </div>
        )}
      </div>
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────
// MODAL PRINCIPAL
// ─────────────────────────────────────────────────────────────
export default function CrearSolicitudModal({ currentCompany, currentUser, onClose, onSuccess }) {
  const [step, setStep]             = useState(1); // 1: elegir tipo, 2: llenar form
  const [tipos, setTipos]           = useState({});
  const [tipoSelected, setTipoSelected] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');

  // Selector de empleado (RRHH)
  const [empleados, setEmpleados]         = useState([]);
  const [busquedaEmp, setBusquedaEmp]     = useState('');

  // Campos del formulario
  const [solicitudBase, setSolicitudBase] = useState({
    asunto: '', descripcion: '', prioridad: 'normal',
    fecha_inicio: '', fecha_fin: '', fecha_retorno: '',
    monto_solicitado: '', plazo_meses: '',
    comentarios_empleado: '',
    employee_id: '',
    empleado_nombre_display: '',
  });
  const [detalles, setDetalles] = useState({});

  const { can } = usePermissionsContext();
  const isRRHH = can('REQUEST_APPROVE');

  useEffect(() => {
    solicitudesAPI.getTipos().then(r => setTipos(r.grouped || {})).catch(() => {});
  }, []);

  // Cargar lista de empleados activos para RRHH
  useEffect(() => {
    if (!isRRHH) return;
    employeesAPI.getAll({ estado: 'activo', limit: 500 })
      .then(r => setEmpleados(r.employees || r.data || []))
      .catch(() => {});
  }, [isRRHH]);

  // Dependientes del empleado (para SEG_EXCLUSION)
  const [dependientes, setDependientes] = useState([]);
  useEffect(() => {
    if (tipoSelected?.codigo !== 'SEG_EXCLUSION') return;
    const empId = solicitudBase.employee_id || currentUser?.employee_id || currentUser?.employeeId;
    if (!empId) return;
    api.get(`/related-persons/employee/${empId}`, { params: { isHealthDependent: true } })
      .then(r => setDependientes(Array.isArray(r.data) ? r.data : r.data?.persons || []))
      .catch(() => setDependientes([]));
  }, [tipoSelected?.codigo, solicitudBase.employee_id]); // eslint-disable-line

  const handleSelectTipo = (tipo) => {
    setTipoSelected(tipo);
    setSolicitudBase(b => ({ ...b, asunto: tipo.nombre }));
    // Inicializar detalles con defaults por tipo
    const defaultDetalles = tipo.codigo?.startsWith('PRES_') ? { quincena_descuento: 'Q2' } : {};
    setDetalles(defaultDetalles);
    setStep(2);
  };

  const handleSubmit = async () => {
    setError('');
    if (!solicitudBase.asunto.trim()) { setError('El asunto es requerido'); return; }
    if (isRRHH && !solicitudBase.employee_id) { setError('Selecciona un empleado'); return; }

    setSaving(true);
    try {
      await solicitudesAPI.crear({
        tipo_solicitud_id:    tipoSelected.id,
        asunto:               solicitudBase.asunto,
        descripcion:          solicitudBase.descripcion || undefined,
        prioridad:            solicitudBase.prioridad,
        fecha_inicio:         solicitudBase.fecha_inicio || undefined,
        fecha_fin:            solicitudBase.fecha_fin || undefined,
        fecha_retorno:        solicitudBase.fecha_retorno || undefined,
        monto_solicitado:     solicitudBase.monto_solicitado || undefined,
        plazo_meses:          solicitudBase.plazo_meses || undefined,
        comentarios_empleado: solicitudBase.comentarios_empleado || undefined,
        employee_id:          solicitudBase.employee_id || undefined,
        detalles,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear la solicitud');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button onClick={() => setStep(1)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors mr-1">
                <ChevronLeft size={16} className="text-gray-500" />
              </button>
            )}
            <FileText size={16} className="text-purple-600" />
            <h2 className="text-base font-bold text-gray-900">
              {step === 1 ? 'Nueva Solicitud' : `Nueva ${tipoSelected?.nombre}`}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* STEP 1: Seleccionar tipo */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">¿Qué tipo de solicitud deseas crear?</p>
              {Object.entries(tipos).map(([cat, tiposList]) => {
                const catCfg = CATEGORIA_CFG[cat];
                if (!catCfg || tiposList.length === 0) return null;
                const { Icon } = catCfg;
                return (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-2">
                      <Icon size={13} className="text-gray-400" />
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{catCfg.label}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {tiposList.map(tipo => (
                        <button
                          key={tipo.id}
                          onClick={() => handleSelectTipo(tipo)}
                          className={`text-left p-3 rounded-xl border text-sm font-medium transition-all hover:scale-[1.01] ${catCfg.color}`}
                        >
                          {tipo.nombre}
                          {tipo.descripcion && (
                            <p className="text-xs opacity-70 font-normal mt-0.5">{tipo.descripcion}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 2: Formulario */}
          {step === 2 && (
            <div className="space-y-4">

              {/* Empleado */}
              {isRRHH ? (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Empleado *</label>
                  {solicitudBase.employee_id ? (
                    <div className="flex items-center justify-between border border-purple-300 bg-purple-50 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-purple-500" />
                        <span className="text-sm font-medium text-purple-800">{solicitudBase.empleado_nombre_display}</span>
                      </div>
                      <button
                        onClick={() => setSolicitudBase(b => ({ ...b, employee_id: '', empleado_nombre_display: '' }))}
                        className="text-xs text-purple-500 hover:text-purple-700"
                      >
                        Cambiar
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <div className="relative">
                        <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Buscar empleado por nombre o código..."
                          value={busquedaEmp}
                          onChange={e => setBusquedaEmp(e.target.value)}
                          className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-purple-400"
                        />
                      </div>
                      {busquedaEmp.length >= 2 && (
                        <div className="border border-gray-200 rounded-lg bg-white max-h-44 overflow-y-auto shadow-sm">
                          {empleados
                            .filter(e => {
                              const q = busquedaEmp.toLowerCase();
                              return (
                                `${e.nombre} ${e.apellido}`.toLowerCase().includes(q) ||
                                (e.employee_id || '').toLowerCase().includes(q)
                              );
                            })
                            .slice(0, 10)
                            .map(e => (
                              <button
                                key={e.id}
                                onClick={() => {
                                  setSolicitudBase(b => ({
                                    ...b,
                                    employee_id: e.id,
                                    empleado_nombre_display: `${e.nombre} ${e.apellido}`,
                                  }));
                                  setBusquedaEmp('');
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-purple-50 transition-colors flex items-center gap-2"
                              >
                                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                                  <span className="text-xs font-bold text-purple-600">{e.nombre?.[0]}</span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-gray-800">{e.nombre} {e.apellido}</p>
                                  <p className="text-xs text-gray-400">{e.employee_id}</p>
                                </div>
                              </button>
                            ))}
                          {empleados.filter(e => {
                            const q = busquedaEmp.toLowerCase();
                            return `${e.nombre} ${e.apellido}`.toLowerCase().includes(q) || (e.employee_id || '').toLowerCase().includes(q);
                          }).length === 0 && (
                            <p className="text-sm text-gray-400 text-center py-3">Sin resultados</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Empleado</label>
                  <div className="flex items-center gap-2 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2">
                    <User size={14} className="text-gray-400" />
                    <span className="text-sm text-gray-700 font-medium">
                      {currentUser?.nombre
                        ? `${currentUser.nombre} ${currentUser.apellido || ''}`.trim()
                        : currentUser?.username}
                    </span>
                    <span className="text-xs text-gray-400 ml-1">(tú)</span>
                  </div>
                </div>
              )}

              {/* Asunto */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Asunto *</label>
                <input
                  type="text"
                  value={solicitudBase.asunto}
                  onChange={e => setSolicitudBase(b => ({ ...b, asunto: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400"
                />
              </div>

              {/* Campos específicos por tipo */}
              <CamposEspecificos
                tipo={tipoSelected}
                detalles={detalles}
                onChange={setDetalles}
                solicitudBase={solicitudBase}
                onBaseChange={setSolicitudBase}
                dependientes={dependientes}
              />

              {/* Descripción adicional */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Descripción adicional</label>
                <textarea
                  rows={3}
                  value={solicitudBase.descripcion}
                  onChange={e => setSolicitudBase(b => ({ ...b, descripcion: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 resize-none focus:outline-none focus:border-purple-400"
                  placeholder="Información adicional relevante..."
                />
              </div>

              {/* Prioridad */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Prioridad</label>
                <select
                  value={solicitudBase.prioridad}
                  onChange={e => setSolicitudBase(b => ({ ...b, prioridad: e.target.value }))}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-purple-400"
                >
                  <option value="baja">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="alta">Alta</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 2 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2 bg-purple-600 text-white text-sm font-semibold rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              <Send size={13} />
              {saving ? 'Enviando...' : 'Enviar Solicitud'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
